import { useAuth } from '@/hooks/useAuth';
import { Navigate, Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

export function LandingPage() {
  const { isAuthenticated, isInitialized, login } = useAuth();

  // If already authenticated, redirect to dashboard
  if (isInitialized && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // Show loading while checking auth state
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-gray-50 dark:bg-dark-900">
      {/* Left: Sign In */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">Spark</span>
          </Link>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Welcome to Spark
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            Your AI-powered development platform. Sign in to start building.
          </p>

          <button
            onClick={() => login()}
            className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-500 hover:to-primary-600 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
            </svg>
            Sign in with Applantus
          </button>

          <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
            Secure authentication powered by Applantus SSO
          </p>

          <div className="mt-8 pt-8 border-t border-gray-200 dark:border-dark-700">
            <p className="text-center text-sm text-gray-600 dark:text-gray-400">
              Don't have an account?{' '}
              <button
                onClick={() => login()}
                className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              >
                Create one for free
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Right: Feature Showcase */}
      <div className="hidden lg:block relative flex-1 bg-gradient-to-br from-primary-500 to-primary-700">
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="max-w-lg text-white">
            <h2 className="text-4xl font-bold mb-4">
              Build full-stack apps with AI
            </h2>
            <p className="text-lg text-white/80">
              Spark is your AI-powered development platform. Describe what you want to build,
              and watch as your application comes to life with production-ready code,
              infrastructure, and deployment.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              <Feature title="AI Chat Interface" description="Describe features in natural language" />
              <Feature title="Live Preview" description="See changes in real-time" />
              <Feature title="Auto Deployment" description="Push to production instantly" />
              <Feature title="Full Stack" description="Frontend, backend, and database" />
            </div>
          </div>
        </div>

        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
      </div>
    </div>
  );
}

function Feature({ title, description }: { title: string; description: string }) {
  return (
    <div className="p-4 bg-white/10 rounded-lg backdrop-blur-sm">
      <h3 className="font-semibold mb-1">{title}</h3>
      <p className="text-sm text-white/70">{description}</p>
    </div>
  );
}
